import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TaskListListOutput } from "@orsane/schema/task-lists";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type React from "react";
import { memo, useCallback, useMemo } from "react";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { trpc } from "../QueryProvider";
import { TaskListBadgeColorShape } from "../TaskListBadge";

export const SortableSidebarMenuItem = memo(
    ({ list, currentTaskListId, id }: { list: TaskListListOutput; currentTaskListId: number | undefined; id: string }) => {
        const { attributes, listeners, transform, transition, isDragging, setNodeRef } = useSortable({
            id: id,
            disabled: false,
        });
        const queryClient = useQueryClient();

        const style = useMemo(
            () => ({
                zIndex: isDragging ? 100 : undefined,
                transform: CSS.Transform.toString(transform),
                transition,
                cursor: isDragging ? "grabbing" : "grab",
            }),
            [isDragging, transform, transition]
        );

        const isActive = currentTaskListId === list.id;

        const handleClick = useCallback(
            (e: React.MouseEvent) => {
                // Only allow navigation if not dragging
                if (isDragging) {
                    e.preventDefault();
                    e.stopPropagation();
                } else {
                    // refresh the tasks when clicking on a list item
                    queryClient.invalidateQueries({
                        queryKey: trpc.task.list.pathKey(),
                    });
                }
            },
            [isDragging]
        );

        return (
            <SidebarMenuItem ref={setNodeRef} key={list.id} style={style} {...attributes} {...listeners} className="w-full">
                <SidebarMenuButton
                    render={
                        <Link title={list.name} to={"/lists/$taskListId"} params={{ taskListId: list.id }} onClick={handleClick}>
                            <div className="flex items-center gap-2 w-full select-none">
                                <TaskListBadgeColorShape color={list.color} />
                                <div className="flex-1 select-none truncate">{list.name}</div>
                            </div>
                        </Link>
                    }
                    isActive={isActive}
                    className={cn([
                        {
                            "bg-red-400": isDragging,
                        },
                    ])}
                ></SidebarMenuButton>
            </SidebarMenuItem>
        );
    }
);
